/// <reference types="@types/google.maps" />
import { LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { INDIVIDUAL, SALES } from '@rma/generic/domain/types';
import { DEFAULT_GOOGLE_MAP_OPTIONS, MAP_TYPES } from '@rma/generic/maps/domain';
import { boundsToSelectionShape, MapMarkerService } from '@rma/generic/maps/util';
import { appendToRmaStaticImageUrl } from '@rma/generic/ng/util';
import { CalloutComponent } from '@rma/public/generic/ui/callout';
import { SkeletonGridComponent } from '@rma/public/generic/ui/skeleton';
import { GoogleMapComponent } from '@rma/public/maps/public/ui-google-map';
import { COMPARE_AGENT_SCOPE } from '../domain/translation-scope';
import { AgentToggleRowComponent } from '../ui-agent-toggle-row/agent-toggle-row.component';
import { CompareAgentsMapAgent, CompareAgentsMapStore } from './compare-agents-map.store';

export type { CompareAgentsMapAgent } from './compare-agents-map.store';

const COMPARE_AGENTS_MAP_PREVIEW_IMAGE = 'compare-agents/compare-agents-map-preview.webp';

@Component({
  selector: 'rma-compare-agents-map-section',
  templateUrl: './compare-agents-map-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CompareAgentsMapStore, MapMarkerService, provideTranslocoScope(COMPARE_AGENT_SCOPE)],
  imports: [
    AgentToggleRowComponent,
    CalloutComponent,
    LowerCasePipe,
    GoogleMapComponent,
    MatProgressSpinnerModule,
    MatSelectModule,
    SkeletonGridComponent,
    TranslocoDirective,
  ],
  host: {
    class: 'block overflow-hidden rounded-lg border border-steel-10 bg-white',
  },
})
export class CompareAgentsMapSectionComponent {
  // the query only resolves once the deferred block activates, which is the viewport gate the markers wait on
  private readonly mapArea = viewChild('mapArea');

  protected readonly scope = `${COMPARE_AGENT_SCOPE}.map`;
  protected readonly store = inject(CompareAgentsMapStore);
  protected readonly profileType = INDIVIDUAL;
  protected readonly markerTypes = MAP_TYPES[SALES];
  protected readonly options: google.maps.MapOptions = {
    ...DEFAULT_GOOGLE_MAP_OPTIONS,
    disableDefaultUI: true,
  };
  protected readonly previewImageUrl = inject(appendToRmaStaticImageUrl)(COMPARE_AGENTS_MAP_PREVIEW_IMAGE);

  public readonly agents = input.required<readonly CompareAgentsMapAgent[]>();
  public readonly gated = input(false);

  constructor() {
    this.store.load(computed(() => ({ agents: this.agents(), gated: this.gated(), visible: !!this.mapArea() })));
  }

  onBoundsChanged(bounds: google.maps.LatLngLiteral[] | null): void {
    if (bounds) {
      this.store.setShape(boundsToSelectionShape(bounds));
    }
  }
}
